/**
 * Home-page ad banner carousel — auto-fetches geo-targeted banners
 * from `/me/ads/home` and renders as a swipeable card list.
 */
import React, { useEffect, useState, useRef } from "react";
import { custApi } from "../../lib/customerApi";

export default function MeHomeBanners() {
  const [banners, setBanners] = useState([]);
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    custApi.get("/me/ads/home").then(r => {
      setBanners(r.data?.banners || []);
    }).catch(() => setBanners([]));
  }, []);

  // Auto-advance flat images across all banners every 5s
  const total = banners.reduce((n, b) => n + (b.images?.length || 0), 0);
  useEffect(() => {
    if (total < 2) return;
    timerRef.current = setInterval(() => {
      setIdx(i => (i + 1) % total);
    }, 5000);
    return () => clearInterval(timerRef.current);
  }, [total]);

  if (banners.length === 0) return null;

  // Flatten { banner, image } pairs so a multi-image banner rotates image-by-image
  const slides = [];
  banners.forEach(b => {
    (b.images || []).forEach(img => slides.push({ b, img }));
  });
  if (slides.length === 0) return null;

  const clickThrough = (b, img) => {
    const url = (img.link_url || b.link_url || "").trim();
    // Record analytics (fire-and-forget)
    custApi.post(`/me/ads/${b.id}/click`).catch(() => {});
    if (!url) return;
    if (url.startsWith("/")) window.location.href = url;
    else window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <section className="px-5 mt-4" data-testid="me-home-ads">
      <div className="relative overflow-hidden rounded-3xl border border-gray-100 shadow-[0_6px_20px_rgba(0,0,0,0.05)] bg-white">
        <div
          className="flex transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${idx * 100}%)`, width: `${slides.length * 100}%` }}
        >
          {slides.map(({ b, img }, i) => (
            <button
              key={`${b.id}-${i}`}
              onClick={() => clickThrough(b, img)}
              data-testid={`me-ad-${b.id}-${i}`}
              className="relative shrink-0 text-left"
              style={{ width: `${100 / slides.length}%` }}
            >
              <div className="relative w-full aspect-[16/8] bg-[#F7F6F2]">
                <img
                  src={img.url}
                  alt={img.alt || b.title}
                  className="w-full h-full object-cover"
                />
                {(b.title || b.subtitle) && (
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 via-black/25 to-transparent p-3">
                    <div className="text-white text-[13px] font-bold leading-tight drop-shadow">{b.title}</div>
                    {b.subtitle && (
                      <div className="text-white/85 text-[11px] mt-0.5 line-clamp-1 drop-shadow">{b.subtitle}</div>
                    )}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>

        {/* Dots */}
        {slides.length > 1 && (
          <div className="absolute inset-x-0 bottom-2 flex items-center justify-center gap-1">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                aria-label={`Go to slide ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === idx ? "w-6 bg-white" : "w-1.5 bg-white/60"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
